import { Body1, Body1Strong, Caption1, makeStyles, tokens } from '@fluentui/react-components';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip as RechartsTooltip } from 'recharts';

import DashboardChartPanel from './DashboardChartPanel';
import { buildDashboardPeriodicReviewData } from '../helpers/dashboardPresentation';
import type { TDashboardViewCounters } from '../types/dashboard.types';

const useStyles = makeStyles({
  content: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXL,
    '@media (max-width: 560px)': {
      alignItems: 'stretch',
      flexDirection: 'column',
    },
  },
  chart: {
    position: 'relative',
    flexShrink: 0,
    width: '200px',
    height: '200px',
  },
  center: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'none',
  },
  legend: {
    display: 'flex',
    flex: 1,
    minWidth: 0,
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  legendRow: {
    display: 'grid',
    gridTemplateColumns: '12px minmax(0, 1fr) auto',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
  },
  swatch: {
    width: '10px',
    height: '10px',
    borderRadius: tokens.borderRadiusCircular,
  },
  label: {
    minWidth: 0,
  },
  value: {
    display: 'flex',
    alignItems: 'baseline',
    gap: tokens.spacingHorizontalXS,
  },
  caption: {
    color: tokens.colorNeutralForeground3,
  },
});

type TProps = {
  counters: TDashboardViewCounters;
};

const DashboardPeriodicReviewChart = ({ counters }: TProps) => {
  const classes = useStyles();
  const data = buildDashboardPeriodicReviewData(counters);
  const total = data.reduce((sum, item) => sum + item.value, 0);
  const percentageTotal = Math.max(total, 1);

  return (
    <DashboardChartPanel title="Periodic review position" caption="Periodic reviews">
      <div className={classes.content}>
        <div className={classes.chart}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                dataKey="value"
                nameKey="label"
                innerRadius={58}
                outerRadius={90}
                paddingAngle={2}
                stroke={tokens.colorNeutralBackground1}
                strokeWidth={2}
              >
                {data.map((entry) => (
                  <Cell key={entry.label} fill={entry.color} />
                ))}
              </Pie>
              <RechartsTooltip
                formatter={(value, name) => [
                  `${Number(value).toLocaleString()} items (${Math.round((Number(value) / percentageTotal) * 100)}%)`,
                  name,
                ]}
              />
            </PieChart>
          </ResponsiveContainer>
          <div className={classes.center}>
            <Body1Strong>{total.toLocaleString()}</Body1Strong>
            <Caption1 className={classes.caption}>Items</Caption1>
          </div>
        </div>
        <div className={classes.legend}>
          {data.map((item) => (
            <div key={item.label} className={classes.legendRow}>
              <span className={classes.swatch} style={{ backgroundColor: item.color }} />
              <Body1 className={classes.label}>{item.label}</Body1>
              <div className={classes.value}>
                <Body1Strong>{item.value.toLocaleString()}</Body1Strong>
                <Caption1 className={classes.caption}>{Math.round((item.value / percentageTotal) * 100)}%</Caption1>
              </div>
            </div>
          ))}
        </div>
      </div>
    </DashboardChartPanel>
  );
};

export default DashboardPeriodicReviewChart;
